/**
 * SpeechToText hook for voice input using Web Speech API
 *
 * Features:
 * - Uses Web Speech API for real-time speech recognition
 * - Streams words to the input as they're recognized
 * - Visual feedback during recording (button style change)
 * - Graceful degradation when API is not available
 */
import { announceListening } from '../lib/assistant_signals'

const SpeechToText = {
  mounted () {
    this.isRecording = false
    this.recognition = null
    this.editor = document.getElementById('message-input')

    // Check for Web Speech API support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

    if (!SpeechRecognition) {
      // Hide button if speech recognition is not supported
      this.el.style.display = 'none'
      console.warn('[SpeechToText] Web Speech API not supported')
      return
    }

    // Initialize speech recognition
    this.recognition = new SpeechRecognition()
    this.recognition.continuous = true
    this.recognition.interimResults = true
    this.recognition.lang = navigator.language || 'en-US'

    // Track the text we've already committed
    this.committedText = ''
    this.lastInterimText = ''

    // Handle results
    this.recognition.onresult = (event) => {
      let interimTranscript = ''
      let finalTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          finalTranscript += transcript
        } else {
          interimTranscript += transcript
        }
      }

      // If we have final results, commit them
      if (finalTranscript) {
        this.committedText += finalTranscript
        this.lastInterimText = ''
      }

      // Update the editor with committed text + current interim
      this.updateEditor(this.committedText + interimTranscript)
    }

    // Handle errors
    this.recognition.onerror = (event) => {
      console.error('[SpeechToText] Error:', event.error)

      if (event.error === 'not-allowed') {
        this.pushEvent('flash', {
          type: 'error',
          message: 'Microphone access denied. Please allow microphone access and try again.'
        })
      } else if (event.error === 'no-speech') {
        // No speech detected - this is normal, don't show error
      } else if (event.error !== 'aborted') {
        this.pushEvent('flash', {
          type: 'error',
          message: `Speech recognition error: ${event.error}`
        })
      }

      this.stopRecording()
    }

    // Handle end of recognition
    this.recognition.onend = () => {
      // If still in recording mode, restart (continuous mode can end unexpectedly)
      if (this.isRecording) {
        try {
          this.recognition.start()
        } catch {
          // Already started or other issue
          this.stopRecording()
        }
      }
    }

    // Click handler
    this.el.addEventListener('click', (e) => {
      e.preventDefault()
      this.toggleRecording()
    })
  },

  toggleRecording () {
    if (this.isRecording) {
      this.stopRecording()
    } else {
      this.startRecording()
    }
  },

  startRecording () {
    if (!this.recognition || !this.editor) {return}

    // Get existing content from editor
    const existingContent = this.editor.textContent || ''

    // Add a space if there's existing content
    if (existingContent.trim()) {
      this.committedText = existingContent + ' '
    } else {
      this.committedText = ''
    }

    this.lastInterimText = ''
    this.isRecording = true
    announceListening(true)

    // Update button appearance
    this.el.classList.add('btn-error', 'animate-pulse')
    this.el.classList.remove('btn-ghost')

    // Update tooltip
    const tooltip = this.el.closest('.tooltip')
    if (tooltip) {
      tooltip.dataset.tip = 'Stop recording'
    }

    try {
      this.recognition.start()
    } catch (e) {
      console.error('[SpeechToText] Failed to start:', e)
      this.stopRecording()
    }
  },

  stopRecording () {
    this.isRecording = false
    announceListening(false)

    if (this.recognition) {
      try {
        this.recognition.stop()
      } catch {
        // Already stopped
      }
    }

    // Reset button appearance
    this.el.classList.remove('btn-error', 'animate-pulse')
    this.el.classList.add('btn-ghost')

    // Reset tooltip
    const tooltip = this.el.closest('.tooltip')
    if (tooltip) {
      tooltip.dataset.tip = 'Voice input'
    }

    // Focus the editor
    if (this.editor) {
      this.editor.focus()
      // Move cursor to end
      const range = document.createRange()
      const sel = window.getSelection()
      range.selectNodeContents(this.editor)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    }
  },

  updateEditor (text) {
    if (!this.editor) {return}

    // Set the text content
    this.editor.textContent = text

    // Trigger input event to update hidden field and placeholder
    this.editor.dispatchEvent(new Event('input', { bubbles: true }))

    // Move cursor to end
    const range = document.createRange()
    const sel = window.getSelection()
    range.selectNodeContents(this.editor)
    range.collapse(false)
    sel.removeAllRanges()
    sel.addRange(range)
  },

  destroyed () {
    // Whoever is showing "listening" would otherwise be left showing it
    if (this.isRecording) { announceListening(false) }
    if (this.recognition) {
      try {
        this.recognition.stop()
      } catch {
        // Already stopped
      }
      this.recognition = null
    }
  }
}

export default SpeechToText
